import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import { DriverTreeCanvas } from "../features/driver-tree/DriverTreeCanvas";
import Hint from "../components/Hint";

type Tree = {
  id: string;
  name: string;
  root_metric_id: string;
  node_count?: number;
  edge_count?: number;
};

export default function DriverTreesPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);

  const trees = useQuery({
    queryKey: ["driver-trees", effectiveTenantId],
    queryFn: () => api.get<Tree[]>("/driver-trees"),
  });

  const createTree = useMutation({
    mutationFn: (vars: { name: string; root_metric_id: string }) =>
      api.post<Tree>("/driver-trees", vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["driver-trees", effectiveTenantId] }),
  });

  useEffect(() => {
    setSelected(null);
  }, [effectiveTenantId]);

  /** UX (Iteration 2): den ersten Baum automatisch öffnen, statt eine leere
   *  Flaeche zu zeigen — bei genau einem Baum ist das immer richtig. */
  useEffect(() => {
    if (!selected && (trees.data?.length ?? 0) > 0) {
      setSelected(trees.data![0].id);
    }
  }, [trees.data, selected]);

  const onNew = () => {
    const name = window.prompt("Name des Driver-Trees (z.B. 'MRR Tree'):");
    if (!name) return;
    const root = window.prompt("Root-Metric-ID (z.B. metric_xyz):");
    if (!root) return;
    createTree.mutate({ name, root_metric_id: root });
  };

  return (
    <div className="page" style={{ display: "grid", gridTemplateRows: "auto auto 1fr", gap: 16, height: "100%" }}>
      <div>
        <h1>Driver-Trees — Ursache und Wirkung</h1>
        <p className="subtitle">
          Ein <Hint term="driver_tree">Driver-Tree</Hint> beantwortet die Frage: <em>Welcher Hebel wirkt auf
          welche Kennzahl?</em> Oben der Wertbeitrag, in der Mitte die Handlungsfelder mit ihren
          Arbeitspaketen aus <Hint term="openproject">OpenProject</Hint>, unten die Kennzahlen mit
          Ist-Wert, Ziel und <Hint term="zielerreichung">Zielerreichung</Hint>. Wählen Sie unten einen Baum aus.
        </p>
      </div>

      <div className="dt-list">
        <div className="dt-list-header">
          <strong>Trees in diesem Mandanten</strong>
          <span className="grow" />
          <button className="btn" onClick={onNew} disabled={createTree.isPending}>+ Neuer Baum</button>
        </div>
        {trees.isLoading && <p>Lade…</p>}
        {trees.error && <p className="error">Fehler beim Laden.</p>}
        {trees.data?.length === 0 && (
          <p className="muted">Noch keine Trees. Lege einen an oder lass den Discovery-Agent vorschlagen.</p>
        )}
        <ul className="tree-chips">
          {(trees.data ?? []).map((t) => (
            <li
              key={t.id}
              className={`tree-chip ${selected === t.id ? "active" : ""}`}
              onClick={() => setSelected(t.id)}
            >
              <strong>{t.name}</strong>
              <span className="muted"> · root {t.root_metric_id}</span>
              {t.node_count != null && (
                <span className="badge">{t.node_count} Knoten</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="dt-canvas-wrap" style={{ minHeight: 480, border: "1px solid var(--border)", borderRadius: 8 }}>
        {!selected && (
          <div className="empty-friendly">
            <strong>Noch kein Baum ausgewählt.</strong>
            <p className="muted">Klicken Sie oben auf einen Baum — dann erscheint hier die Wirkungslandkarte.</p>
          </div>
        )}
        {selected && <DriverTreeCanvas treeId={selected} />}
      </div>
    </div>
  );
}
